import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { apiBase } from "@/api/rest/core/apiClient";
import {
  deleteDevelopmentLogo,
  updateDevelopmentLogoSize,
  uploadDevelopmentLogo,
} from "@/api/rest/services/adminService";
import Button from "@/components/common/Button";

import NumberStepper from "./NumberStepper";

interface ViewBox {
  minX: number;
  minY: number;
  width: number;
  height: number;
}

interface LogoEditorProps {
  developmentId: number;
  logoUrl: string | null;
  maxWidth: number;
  maxHeight: number;
}

export default function LogoEditor({ developmentId, logoUrl, maxWidth, maxHeight }: LogoEditorProps) {
  const queryClient = useQueryClient();
  const [showViewBoxEditor, setShowViewBoxEditor] = useState(false);

  // Local state for display size to avoid scroll jumps during editing
  const [localWidth, setLocalWidth] = useState(maxWidth);
  const [localHeight, setLocalHeight] = useState(maxHeight);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local state when props change (e.g., after upload)
  useEffect(() => {
    setLocalWidth(maxWidth);
    setLocalHeight(maxHeight);
  }, [maxWidth, maxHeight]);

  const uploadLogoMutation = useMutation({
    mutationFn: ({ developmentId, file }: { developmentId: number; file: File }) =>
      uploadDevelopmentLogo(developmentId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-development", developmentId] });
    },
  });

  const deleteLogoMutation = useMutation({
    mutationFn: (developmentId: number) => deleteDevelopmentLogo(developmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-development", developmentId] });
    },
  });

  const updateLogoSizeMutation = useMutation({
    mutationFn: ({
      developmentId,
      maxWidth,
      maxHeight,
    }: {
      developmentId: number;
      maxWidth: number;
      maxHeight: number;
    }) => updateDevelopmentLogoSize(developmentId, maxWidth, maxHeight),
    // Don't invalidate immediately - we use local state for instant feedback
  });

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== "image/svg+xml") {
        alert("Only SVG files are allowed");
        return;
      }
      uploadLogoMutation.mutate({ developmentId, file });
    }
    e.target.value = "";
  };

  // Debounced save to avoid scroll jumps
  const debouncedSaveSize = (width: number, height: number) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      updateLogoSizeMutation.mutate({ developmentId, maxWidth: width, maxHeight: height });
    }, 500);
  };

  const handleWidthChange = (value: number) => {
    setLocalWidth(value);
    debouncedSaveSize(value, localHeight);
  };

  const handleHeightChange = (value: number) => {
    setLocalHeight(value);
    debouncedSaveSize(localWidth, value);
  };

  const fullLogoUrl = logoUrl ? `${apiBase}${logoUrl}` : null;

  return (
    <div className="space-y-4">
      {/* Preview Section */}
      <div>
        <p className="text-xs text-gray-500 mb-2">Preview (actual navbar size):</p>
        <div
          className="p-4 bg-white rounded border flex items-center justify-center"
          style={{ width: `${localWidth + 32}px`, height: `${localHeight + 32}px` }}
        >
          {fullLogoUrl ? (
            <img
              src={fullLogoUrl}
              alt="Development Logo"
              style={{ maxWidth: `${localWidth}px`, maxHeight: `${localHeight}px` }}
            />
          ) : (
            <span className="text-gray-400 text-sm">No logo uploaded</span>
          )}
        </div>
      </div>

      {/* Controls Section */}
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className="flex flex-col gap-3">
          <p className="text-xs text-gray-500">Display Size</p>
          <div className="flex flex-wrap gap-3">
            <NumberStepper
              label="W:"
              value={localWidth}
              onChange={handleWidthChange}
              min={50}
              max={600}
              step={10}
              disabled={!logoUrl}
            />
            <NumberStepper
              label="H:"
              value={localHeight}
              onChange={handleHeightChange}
              min={20}
              max={150}
              step={5}
              disabled={!logoUrl}
            />
          </div>

          {logoUrl && (
            <button
              onClick={() => setShowViewBoxEditor(true)}
              className="btn-custom text-sm text-dobu-orange hover:text-dobu-orange/80 text-left"
            >
              Crop whitespace (edit viewBox)
            </button>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        <label className="btn-custom px-4 py-2 bg-dobu-orange text-white text-sm rounded hover:bg-dobu-orange/90 cursor-pointer">
          {uploadLogoMutation.isPending ? "Uploading..." : "Upload SVG"}
          <input
            type="file"
            accept="image/svg+xml"
            onChange={handleLogoUpload}
            className="hidden"
            disabled={uploadLogoMutation.isPending}
          />
        </label>
        {logoUrl && (
          <Button
            variant="danger"
            onClick={() => deleteLogoMutation.mutate(developmentId)}
            disabled={deleteLogoMutation.isPending}
          >
            {deleteLogoMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        )}
      </div>

      {/* ViewBox Editor Modal */}
      {showViewBoxEditor && fullLogoUrl && (
        <ViewBoxEditorModal
          logoUrl={fullLogoUrl}
          developmentId={developmentId}
          onClose={() => setShowViewBoxEditor(false)}
        />
      )}
    </div>
  );
}

interface ViewBoxEditorModalProps {
  logoUrl: string;
  developmentId: number;
  onClose: () => void;
}

function ViewBoxEditorModal({ logoUrl, developmentId, onClose }: ViewBoxEditorModalProps) {
  const queryClient = useQueryClient();
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [originalViewBox, setOriginalViewBox] = useState<ViewBox | null>(null);
  const [viewBox, setViewBox] = useState<ViewBox | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: ({ developmentId, file }: { developmentId: number; file: File }) =>
      uploadDevelopmentLogo(developmentId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-development", developmentId] });
      onClose();
    },
  });

  useEffect(() => {
    const fetchSvg = async () => {
      try {
        setLoading(true);
        const response = await fetch(logoUrl);
        const text = await response.text();
        setSvgContent(text);

        // Parse the viewBox
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, "image/svg+xml");
        const svg = doc.querySelector("svg");

        if (!svg) {
          setError("Invalid SVG file");
          return;
        }

        const viewBoxAttr = svg.getAttribute("viewBox");
        if (viewBoxAttr) {
          const parts = viewBoxAttr
            .trim()
            .split(/[\s,]+/)
            .map(Number);
          if (parts.length === 4 && parts.every((p) => !isNaN(p))) {
            const vb = { minX: parts[0], minY: parts[1], width: parts[2], height: parts[3] };
            setOriginalViewBox(vb);
            setViewBox(vb);
          } else {
            setError("Invalid viewBox format");
          }
        } else {
          // Try to get dimensions from width/height attributes
          const width = parseFloat(svg.getAttribute("width") || "100");
          const height = parseFloat(svg.getAttribute("height") || "100");
          const vb = { minX: 0, minY: 0, width, height };
          setOriginalViewBox(vb);
          setViewBox(vb);
        }
      } catch {
        setError("Failed to load SVG");
      } finally {
        setLoading(false);
      }
    };

    fetchSvg();
  }, [logoUrl]);

  const handleSave = () => {
    if (!svgContent || !viewBox) {
      return;
    }

    // Modify the SVG with the new viewBox
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgContent, "image/svg+xml");
    const svg = doc.querySelector("svg");

    if (svg) {
      svg.setAttribute("viewBox", `${viewBox.minX} ${viewBox.minY} ${viewBox.width} ${viewBox.height}`);
      const serializer = new XMLSerializer();
      const newSvgContent = serializer.serializeToString(doc);
      const blob = new Blob([newSvgContent], { type: "image/svg+xml" });
      const file = new File([blob], "logo.svg", { type: "image/svg+xml" });
      uploadMutation.mutate({ developmentId, file });
    }
  };

  const handleReset = () => {
    if (originalViewBox) {
      setViewBox({ ...originalViewBox });
    }
  };

  const updateViewBox = (field: keyof ViewBox, value: number) => {
    if (viewBox) {
      setViewBox({ ...viewBox, [field]: value });
    }
  };

  const previewViewBox = viewBox ? `${viewBox.minX} ${viewBox.minY} ${viewBox.width} ${viewBox.height}` : "";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Edit ViewBox (Crop Whitespace)</h2>
            <button
              onClick={onClose}
              className="btn-custom p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {loading && <div className="text-center py-8 text-gray-500">Loading SVG...</div>}

          {error && <div className="text-center py-8 text-red-500">{error}</div>}

          {!loading && !error && viewBox && (
            <div className="space-y-6">
              {/* Preview */}
              <div>
                <p className="text-sm text-gray-600 mb-2">Preview:</p>
                <div className="p-4 bg-gray-100 rounded border flex items-center justify-center min-h-[150px]">
                  <svg
                    viewBox={previewViewBox}
                    className="max-w-full max-h-[200px] border border-dashed border-gray-400 bg-white"
                    style={{ width: "200px", height: "auto" }}
                    dangerouslySetInnerHTML={{
                      __html: svgContent?.replace(/<svg[^>]*>/, "").replace(/<\/svg>/, "") || "",
                    }}
                  />
                </div>
              </div>

              {/* ViewBox Controls */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Min X (left crop)</label>
                  <NumberStepper
                    value={viewBox.minX}
                    onChange={(v) => updateViewBox("minX", v)}
                    min={-500}
                    max={500}
                    step={1}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Min Y (top crop)</label>
                  <NumberStepper
                    value={viewBox.minY}
                    onChange={(v) => updateViewBox("minY", v)}
                    min={-500}
                    max={500}
                    step={1}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Width</label>
                  <NumberStepper
                    value={viewBox.width}
                    onChange={(v) => updateViewBox("width", v)}
                    min={1}
                    max={2000}
                    step={1}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Height</label>
                  <NumberStepper
                    value={viewBox.height}
                    onChange={(v) => updateViewBox("height", v)}
                    min={1}
                    max={2000}
                    step={1}
                  />
                </div>
              </div>

              <p className="text-xs text-gray-500">
                Tip: Increase Min X/Y to crop from left/top. Decrease Width/Height to crop from right/bottom.
              </p>

              {/* Action Buttons */}
              <div className="flex justify-between pt-4 border-t">
                <Button variant="outline" onClick={handleReset}>
                  Reset
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={handleSave} disabled={uploadMutation.isPending}>
                    {uploadMutation.isPending ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
